import {
    createContext,
    Dispatch,
    ReactNode,
    SetStateAction,
    useCallback,
    useContext,
    useEffect,
    useState
} from "react";

import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { JiraLogo } from "@atlaskit/logo";

import {
    ufoEventAccountCreation,
    trackFormSubmit,
    trackInvalidEmail,
    trackRecaptchaFailure,
    trackRedirectCheckEmail,
    trackSignFormLoad,
    trackSignupFailure,
    trackSubmitSuccess,
    trackTimeToFormReady,
    ProductDataDetail,
    Bundle,
    Edition,
    getNameYourSiteRedirectUrl,
    getPrimaryProductKeyFromBundle,
    getProductsFromBundle,
    getContinueParam,
    getLocalePath,
    UfoCustomTimingKey,
    getMethodsAvailableRedirection,
    getRecaptcha
} from "@/components/signup/utils";
import { useBxpWebStargateClient } from "@/libraries/bxp-web-stargate-client/src/index";
import { useLocation } from "@/utils/location-provider";

export interface SignupFormContext {
    bundle: Bundle;
    products: ProductDataDetail[];
    isEmbedded?: boolean;
    email: string;
    formError: boolean;
    hasSubmitted: boolean;
    isSubmitting: boolean;
    identityCobranding: string;
    isEmailValid: boolean;
    isReadyForInvalid: boolean;
    onSubmitWithReCAPTCHA: ({ email }: { email: string }) => Promise<void>;
    productKey: string;
    edition: Edition;
    productLogos: Array<typeof JiraLogo>;
    productName: string;
    redirectTo: string;
    setIsEmailValid: Dispatch<SetStateAction<boolean>>;
    setIsReadyForInvalid: Dispatch<SetStateAction<boolean>>;
    showProductLogo: boolean;
    typeAheadEmail: string;
    setTypeAheadEmail: Dispatch<SetStateAction<string>>;
    customHeader?: string;
}

const { IDENTITY_USER_SIGNUP_API } = UfoCustomTimingKey;

export const SignupFormContext = createContext<SignupFormContext | null>(null);

export const useSignupFormContext = (): SignupFormContext => {
    const context = useContext(SignupFormContext);
    if (!context) {
        throw new Error(
            "useSignupFormContext must be used within a SignupFormContextProvider"
        );
    }
    return context;
};

export const SignupFormContextProvider = ({
    bundle,
    children,
    edition = "free",
    isEmbedded = false,
    useCurrentRedirect,
    customHeader
}: {
    bundle: Bundle;
    children: ReactNode;
    edition?: Edition;
    isEmbedded?: boolean;
    useCurrentRedirect?: boolean;
    customHeader?: string;
}) => {
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const intl = useIntl();
    const location = useLocation();
    const [isEmailValid, setIsEmailValid] = useState(false);
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const [isReadyForInvalid, setIsReadyForInvalid] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formError, setFormError] = useState(false);
    const [email, setEmail] = useState("");
    const [typeAheadEmail, setTypeAheadEmail] = useState("");
    const [onSubmitProps, setOnSubmitProps] = useState<
        { email: string } | undefined
    >(undefined);
    const [recaptchaFailedToLoad, setRecaptchaFailedToLoad] =
        useState<boolean>(false);
    const [
        recaptchaRetryTimeRemainingSecond,
        setRecaptchaRetryTimeRemainingSecond
    ] = useState(60);
    const localePath = getLocalePath(intl.locale);
    const signupClient = useBxpWebStargateClient();
    const { executeRecaptcha } = useGoogleReCaptcha();

    const continueParam = getContinueParam(location);
    const redirectTo = useCurrentRedirect
        ? window.location.href
        : continueParam
        ? continueParam
        : getNameYourSiteRedirectUrl({
              bundle,
              edition,
              localePath
          });
    const products = getProductsFromBundle(bundle);

    // default experiment params
    let identityCobranding = "wac";
    if (bundle === "confluence") {
        identityCobranding = "wac--otp-confluence";
    } else if (bundle === "jira-software") {
        identityCobranding = "wac--otp-jira";
    }

    useEffect(() => {
        trackSignFormLoad(createAnalyticsEvent);
        trackTimeToFormReady(createAnalyticsEvent, performance.now());
    }, [createAnalyticsEvent]);

    // Embedded form does not show product logo
    const showProductLogo = !isEmbedded;
    const productKey = products.map(({ productKey }) => productKey).join(",");
    const productName = products
        .map(({ productName }) => productName)
        .join(" + ");
    const productLogos = products.map(({ productLogo }) => productLogo);

    const onSubmitWithReCAPTCHA = useCallback(
        async ({ email }: { readonly email: string }) => {
            setIsReadyForInvalid(true);
            if (!email || !isEmailValid) {
                trackInvalidEmail(createAnalyticsEvent);
                return;
            }
            setOnSubmitProps({ email });
            if (!executeRecaptcha && recaptchaRetryTimeRemainingSecond > 0) {
                // set trigger that will wait for Recaptcha retry logic
                setFormError(true);
                setIsSubmitting(true);
                setRecaptchaFailedToLoad(true);
                return;
            }
            setRecaptchaFailedToLoad(false);

            // Start UFO to begin duration measurements for account creation
            ufoEventAccountCreation.start({
                productKey: getPrimaryProductKeyFromBundle(bundle),
                webPlatform: "bxp-ui"
            });

            const gRecaptchaResponse = await getRecaptcha(
                email,
                executeRecaptcha
            );

            ufoEventAccountCreation.setCustomData({
                gRecaptchaResponse: gRecaptchaResponse ?? "none",
                executeRecaptchaExists: Boolean(executeRecaptcha),
                reactComponent: "SignupFormContextProvider"
            });

            // Handle missing reCAPTCHA response
            if (!gRecaptchaResponse) {
                trackRecaptchaFailure(
                    createAnalyticsEvent,
                    "bxp-ui-signup-form"
                );
                const invalidRecaptchaError = new Error(
                    "Missing reCAPTCHA token"
                );
                ufoEventAccountCreation.failure({
                    customData: {
                        reactComponent: "SignupFormContextProvider"
                    },
                    error: invalidRecaptchaError
                });
                throw invalidRecaptchaError;
            }

            trackFormSubmit(
                createAnalyticsEvent,
                products[0]?.productKey,
                edition
            );

            const existingUserRedirectUrl =
                await getMethodsAvailableRedirection({
                    email,
                    application: identityCobranding,
                    signupClient,
                    createAnalyticsEvent,
                    successRedirect: redirectTo
                }).catch((error) => {
                    setFormError(true);
                    trackSignupFailure(createAnalyticsEvent);
                    throw new Error(error);
                });
            if (existingUserRedirectUrl) {
                window.location.assign(
                    existingUserRedirectUrl.replace("/initiate/password", "")
                );
                return;
            }

            const newUserRedirectTo = redirectTo + `&n2n=true`;

            ufoEventAccountCreation.startSubstep(IDENTITY_USER_SIGNUP_API);

            await signupClient
                .userSignup({
                    application: identityCobranding,
                    displayName: "",
                    email,
                    gRecaptchaResponse: gRecaptchaResponse,
                    locale: intl.locale,
                    products: products.map(({ productKey }) => ({
                        productKey,
                        edition
                    })),
                    redirectTo: newUserRedirectTo
                })
                .then(({ otpRedirect }: { otpRedirect?: string }) => {
                    ufoEventAccountCreation.endSubstep(
                        IDENTITY_USER_SIGNUP_API
                    );

                    if (!otpRedirect) {
                        throw new Error("No OTP redirect");
                    }

                    // Reliability SLO "good event" metric emitted for submit account creation
                    ufoEventAccountCreation.success({
                        customData: {
                            reactComponent: "SignupFormContextProvider"
                        }
                    });

                    window.location.assign(otpRedirect);
                    setHasSubmitted(true);
                })
                .catch((error: string) => {
                    // Reliability SLO "bad event" metric emitted for submit account creation
                    ufoEventAccountCreation.endSubstep(
                        IDENTITY_USER_SIGNUP_API
                    );
                    ufoEventAccountCreation.failure({
                        customData: {
                            reactComponent: "SignupFormContextProvider"
                        },
                        error
                    });

                    setFormError(true);
                    trackSignupFailure(createAnalyticsEvent);
                    throw new Error(error);
                });

            trackSubmitSuccess(createAnalyticsEvent);
            trackRedirectCheckEmail(createAnalyticsEvent);
            setEmail(email);
        },
        [
            bundle,
            createAnalyticsEvent,
            edition,
            executeRecaptcha,
            identityCobranding,
            intl.locale,
            isEmailValid,
            products,
            redirectTo,
            signupClient
        ]
    );

    useEffect(() => {
        if (recaptchaFailedToLoad && recaptchaRetryTimeRemainingSecond <= 0) {
            ufoEventAccountCreation.failure({
                customData: {
                    reactComponent: "SignupFormContextProvider",
                    recaptchaRetryTimeRemainingSecond: 0
                },
                error: new Error("Recaptcha retry failed")
            });
        }
        if (onSubmitProps && recaptchaFailedToLoad && executeRecaptcha) {
            onSubmitWithReCAPTCHA(onSubmitProps)
                .then(() => {
                    ufoEventAccountCreation.success({
                        customData: {
                            reactComponent: "SignupFormContextProvider",
                            analyticsEvent: "succeeded",
                            recaptchaRetryTimeRemainingSecond
                        }
                    });
                })
                .catch((error) => {
                    setFormError(true);
                    setIsSubmitting(false);
                    ufoEventAccountCreation.failure({
                        customData: {
                            reactComponent: "SignupFormContextProvider",
                            analyticsEvent: "errored",
                            recaptchaRetryTimeRemainingSecond
                        },
                        error
                    });
                    return;
                });
        }
    }, [
        executeRecaptcha,
        recaptchaFailedToLoad,
        onSubmitProps,
        recaptchaRetryTimeRemainingSecond
    ]);

    useEffect(() => {
        if (!recaptchaFailedToLoad) return;
        if (recaptchaRetryTimeRemainingSecond <= 0) return;
        const intervalId = setInterval(() => {
            setRecaptchaRetryTimeRemainingSecond(
                recaptchaRetryTimeRemainingSecond - 1
            );
        }, 1000);
        return () => clearInterval(intervalId);
    }, [recaptchaFailedToLoad, recaptchaRetryTimeRemainingSecond]);

    return (
        <SignupFormContext.Provider
            value={{
                bundle,
                isEmbedded,
                email,
                formError,
                hasSubmitted,
                isSubmitting,
                identityCobranding,
                isEmailValid,
                isReadyForInvalid,
                onSubmitWithReCAPTCHA,
                productKey,
                edition,
                productLogos,
                products,
                productName,
                redirectTo,
                setIsEmailValid,
                setIsReadyForInvalid,
                showProductLogo,
                typeAheadEmail,
                setTypeAheadEmail,
                customHeader
            }}
        >
            {children}
        </SignupFormContext.Provider>
    );
};
